import type { AppThunk } from 'src/store';
import { Station } from 'src/types/station';
import { api } from 'src/utils/api';
import axios from 'src/utils/axios';
import { removeHTML } from 'src/utils/common';

export const emptyListingObject = {
  page: -1,
  totalPages: 1,
  totalCount: 1,
  rows: [],
  pageSize: 1,
  skip: 0,
};

const genericActions = (actions: any, resource: string, emptyObject: any) => {
  const getAll = (params = {}): AppThunk => async (dispatch) => {
    dispatch(actions.setLoading({ loading: true }));
    const { data, status } = await api(resource, 'get', params);
    const getData = (data: any, error: boolean, success: boolean, message: string) => ({
      records: data, error, success, message, loading: false,
    });
    if (status === 200)
      dispatch(actions.getAll(getData(data, false, false, '')));
    else {
      let msg = `An error while loading ${resource}!`;
      if (data.detail)
        msg = `${removeHTML(data.detail)}`;
      dispatch(actions.getAll(getData([], true, false, msg)));
    }
  };

  const get = (id: number): AppThunk => async (dispatch) => {
    const getData = (data: any, error: boolean, success: boolean, message: string) => ({
      selectedRecord: data, error, success, message, loading: false,
    });
    dispatch(actions.setLoading({ loading: true }));
    dispatch(actions.resetSelected());
    const { data, status } = await api(`${resource}/${id}`, 'get');
    if (status === 200)
      dispatch(actions.get(getData(data, false, false, '')));
    else {
      let msg = 'An error while loading details!';
      if (data.detail)
        msg = `${removeHTML(data.detail)}`;
      dispatch(actions.get(getData(emptyObject, true, false, msg)));
    }
  };

  const set = (obj: any, objName: string): AppThunk => async (dispatch) => {
    dispatch(actions.setLoading({ loading: true }));
    const { status, data } = await api(`${resource}`, 'post', obj);
    const getData = (error: boolean, success: boolean, message: string) => ({
      error, success, message, loading: false,
    });

    if (status === 200) {
      dispatch(actions.set(getData(false, true, `${objName} has been created.`)));
    } else {
      let msg = `An error while creating ${objName}!`;
      if (data.detail)
        msg = `${removeHTML(data.detail)}`;
      dispatch(actions.set(getData(true, false, msg)));
    }
  };

  const put = (id: number, obj: any, objName: string): AppThunk => async (dispatch) => {
    dispatch(actions.setLoading({ loading: true }));
    const { status, data } = await api(`${resource}/${id}`, 'put', obj);
    const getData = (error: boolean, success: boolean, message: string) => ({
      error, success, message, loading: false,
    });

    if (status === 200) {
      dispatch(actions.set(getData(false, true, `${objName} has been updated.`)));
    } else {
      let msg = `An error while updating a ${objName}!`;
      if (data.detail)
        msg = `${removeHTML(data.detail)}`;
      dispatch(actions.set(getData(true, false, msg)));
    }
  };

  const patch = (id: number, objName: string, patchName: string, patchValue: any): AppThunk => async (dispatch) => {
    const { status, data } = await api(`${resource}/${id}/${patchName}/${patchValue}`, 'patch');
    const getData = (error: boolean, success: boolean, message: string) => ({
      error, success, message, loading: null,
    });

    if (status === 200) {
      dispatch(actions.set(getData(false, true, `The ${patchName} of ${objName} has been changed.`)));
    } else {
      let msg = `An error while changing ${patchName} of ${objName}!`;
      if (data.detail)
        msg = `${removeHTML(data.detail)}`;
      dispatch(actions.set(getData(true, false, msg)));
    }
  };

  const remove = (id: number, objName: string): AppThunk => async (dispatch) => {
    dispatch(actions.setLoading({ loading: true }));
    const { status, data } = await api(`${resource}/${id}`, 'delete');
    const getData = (error: boolean, success: boolean, message: string) => ({
      error, success, message, loading: false,
    });

    if (status === 200) {
      dispatch(actions.remove(getData(false, true, `${objName} has been removed.`)));
    } else {
      let msg = `An error while removing ${objName}!`;
      if (data.detail)
        msg = `${removeHTML(data.detail)}`;
      dispatch(actions.remove(getData(true, false, msg)));
    }
  };

  const resetError = (): AppThunk => async (dispatch) => {
    dispatch(actions.resetError({}));
  };

  const resetSuccess = (): AppThunk => async (dispatch) => {
    dispatch(actions.resetSuccess({}));
  };

  return { getAll, get, set, put, patch, remove, resetError, resetSuccess };
};

export const genericMockActions = (actions: any, resource: string, emptyObject: any) => {
  const getAll = (params = {}): AppThunk => async (dispatch) => {
    dispatch(actions.setLoading({ loading: true }));
    const { data, status } = await axios.get<{ data: Station[]; }>(`/api/${resource}`, { params });
    const getData = (data: any, error: boolean, success: boolean, message: string) => ({
      records: data, error, success, message, loading: false,
    });
    if (status === 200)
      dispatch(actions.getAll(getData(data, false, false, '')));
    else
      dispatch(actions.getAll(getData([], true, false, `An error while loading ${resource}!`)));
  };

  return { getAll };
};

export default genericActions;